import React, { useState } from 'react';
import { TestNoteIcons } from '../../../../../icons/icons';
import { Dialog } from '../../../../../ui';
import { PencilIcon, TrashIcon } from '../../../../../ui/icons/icons';
import { editApplicantNote } from '../../../../../utils/api_service/JobOpenings';
import { ConversationNote, NoteStructure } from '../../../../../utils/types/JobOpenings';
import { DIALOG_TYPE } from '../../../Constants/DialogTypes';

interface INotesTab {
    notes: ConversationNote[] | undefined,
    onDelete: (id: string) => Promise<void>,
    getJobApplicants: () => Promise<void>,
    isModalOpen: boolean,
    setIsModalOpen: (value: (((prevState: boolean) => boolean) | boolean)) => void
}

export const NotesTab = ({ notes, onDelete, getJobApplicants, isModalOpen, setIsModalOpen }: INotesTab) => {

  // Determine if we have notes to display
  const notesData = notes;
  // const notesData = notes && Array.isArray(notes) && notes.length > 0 ? notes : dummyNotes;
  const hasNotes = notesData && notesData.length > 0;
  const userEmail = localStorage.getItem('email');
  const [editingNoteId, setEditingNoteId] = React.useState<string | null>(null);

  const [deleteId, setDeleteId] = useState<string>('');

  const [editedNote, setEditedNote] = React.useState<NoteStructure>({
    noteBody: '',
    noteSubject: '',
    noteType: '',
  });

  const conversationIcons: Record<string, string> = {
    Email: '📧',
    Phone: '📞',
    Person: '🤝',
    Video: '🎥',
    Other: '📝',
  };

  const conversationColors: Record<string, string> = {
    Email: 'bg-blue-50 text-blue-700 border-blue-200',
    Phone: 'bg-green-50 text-green-700 border-green-200',
    Person: 'bg-purple-50 text-purple-700 border-purple-200',
    Video: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    Other: 'bg-gray-50 text-gray-700 border-gray-200',
  };

  const formatDate = (timestamp: string) => {
    const unix = Number(timestamp);

    if (isNaN(unix)) {
      throw new Error('Invalid Unix timestamp string');
    }

    const date = new Date(unix * 1000);

    const day = String(date.getDate()).padStart(2, '0');

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];

    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  if (!hasNotes) {
    return (
      <div className='flex items-center justify-center h-64 animate-fadeIn'>
        <div className='text-center'>
          <TestNoteIcons className='w-16 h-16 mx-auto text-gray-300 mb-4'/>
          <p className='text-gray-500 text-sm font-medium'>No notes available</p>
          <p className='text-gray-400 text-xs mt-2'>
            Add notes using the &quot;Add Note&quot; button
          </p>
        </div>
      </div>
    );
  }

  const handleEdit = (note: ConversationNote) => {
    setEditingNoteId(note.id);
    setEditedNote({
      noteBody: note.noteBody,
      noteSubject: note.noteSubject,
      noteType: note.noteType,
    });

  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSave = async (note: ConversationNote) => {
    console.log('Saving note:', {
      id: note.id,
      editedNote: editedNote,
    });

    try {
      const response = await editApplicantNote(note.id, editedNote);
      if (response.ok) {
        getJobApplicants();
      }

    } catch (e) {
      console.log(e);
    }

    setEditingNoteId(null);
  };

  const handleCancel = () => {
    setEditingNoteId(null);
  };

  return (
    <div className='space-y-4 p-4 animate-fadeIn'>
      <div className='flex items-center justify-between mb-2'>
        <h3 className='text-base font-semibold text-gray-900 flex items-center gap-2'>
          <TestNoteIcons className='h-5 w-5 text-yellow-600'/>
          Conversation History ({ notesData.length })
        </h3>
      </div>

      { notes.map((note: ConversationNote) => (
        <div
            key={note.id}
            className='bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-200'
        >
          { /* Note Header */ }
          <div className='bg-gradient-to-r from-gray-50 to-white px-4 py-3 border-b border-gray-100'>
            <div className='flex items-start justify-between gap-3'>

              <div className='flex items-start gap-3 flex-1'>
                <div
                    className={`flex items-center justify-center w-10 h-10 rounded-lg border-2 ${
                      conversationColors[note.noteType]
                    }`}
                >
                  <span className='text-xl'>
                    { conversationIcons[note.noteType] }
                  </span>
                </div>
                <div className='flex-1 min-w-0'>
                  { editingNoteId === note.id ? (
                    <input
                        type='text'
                        value={editedNote.noteSubject}
                        onChange={(e) =>
                          setEditedNote(prev => ({ ...prev, noteSubject: e.target.value }))
                        }
                        className='w-full text-sm font-medium text-gray-900 border rounded-md px-2 py-1'
                    />

                  ) : (
                    <h4 className='font-semibold text-gray-900 text-sm mb-1'>
                      { note.noteSubject }
                    </h4>
                  ) }

                  <div className='flex flex-wrap items-center gap-2 text-xs text-gray-500'>

                    <span>•</span>
                    <span className='flex items-center gap-1'>
                      <svg
                          className='w-3.5 h-3.5'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                      >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
                        />
                      </svg>
                      { note.author ? (
                        <>
                          { `${note.author.firstName ?? ''} ${note.author.lastName ?? ''}`.trim() }
                          { note.author.email === userEmail && ' (You)' }
                        </>
                      ) : (
                        'Unknown'
                      ) }

                    </span>
                  </div>
                </div>
              </div>
              { editingNoteId === note.id ? (
                <select
                    value={editedNote.noteType}
                    onChange={(e) =>
                      setEditedNote(prev => ({ ...prev, noteType: e.target.value }))
                    }
                    className='text-xs px-2 py-1 border rounded-md bg-white capitalize'
                >
                  { Object.keys(conversationIcons).map((type) => (
                    <option key={type} value={type}>
                      { type }
                    </option>
                  )) }
                </select>

              ) : (
                <span
                    className={`text-xs font-medium px-2 py-1 rounded-md border ${
                      conversationColors[note.noteType]
                    } capitalize whitespace-nowrap`}
                >
                  { note.noteType.replace('-', ' ') }
                </span>
              ) }

            </div>
          </div>

          { /* Note Content */ }
          <div className='p-4'>
            { editingNoteId === note.id ? (
              <textarea
                  value={editedNote.noteBody}
                  onChange={(e) =>
                    setEditedNote(prev => ({ ...prev, noteBody: e.target.value }))
                  }
                  className='w-full text-sm text-gray-700 border rounded-md p-2'
                  rows={4}
              />
            ) : (
              <p className='text-sm text-gray-700 leading-relaxed whitespace-pre-line'>
                { note.noteBody }
              </p>
            ) }

          </div>

          { /* Note Footer */ }
          <div className='px-4 py-2 bg-gray-50 border-t border-gray-100 flex items-center justify-between'>
            <p className='text-xs text-gray-500'>
              Added on { formatDate(note.createdAt) }
            </p>

            { /* 🔥 Only show if this user created the note */ }
            { note.author?.email === userEmail && (
              <div className='flex items-center gap-2'>

                { editingNoteId === note.id ? (
                  <>
                    <button
                        onClick={() => handleSave(note)}
                        className='text-[11px] px-2 py-1 rounded-md bg-green-100 text-green-700 hover:bg-green-200 transition'
                    >
                      Save
                    </button>

                    <button
                        onClick={handleCancel}
                        className='text-[11px] px-2 py-1 rounded-md bg-red-100 text-red-700 hover:bg-red-200 transition'
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                        key='edit'
                        type='button'
                        className='p-1 text-gray-500 hover:text-green-700'
                        onClick={() => handleEdit(note)}
                    >
                      <PencilIcon className='w-4 h-4'/>
                    </button>

                    <button
                        key='delete'
                        type='button'
                        className='p-1 text-gray-500 hover:text-red-500'
                        onClick={() => {
                          setDeleteId(note.id);
                          setIsModalOpen(true);
                        }}
                    >
                      <TrashIcon className='w-4 h-4'/>
                    </button>

                    { /*<button*/ }
                    { /*    onClick={() => {*/ }
                    { /*      setDeleteId(note.id);*/ }
                    { /*      setIsModalOpen(true);*/ }
                    { /*    }}*/ }
                    { /*    className='text-[11px] px-2 py-1 rounded-md bg-red-100 text-red-700 hover:bg-red-200 transition'*/ }
                    { /*>*/ }
                    { /*  Delete*/ }
                    { /*</button>*/ }

                  </>
                ) }

              </div>
            ) }
          </div>
        </div>
      )) }
      <Dialog
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title='Are you sure you want to delete this note?'
          dialogType={DIALOG_TYPE.ERROR}
          justify='center'
          confirmButtonLable='Delete Conversation Note'
          cancelButtonLable='Cancel'
          handleApply={() => onDelete(deleteId)}
      >
      </Dialog>
    </div>
  );
};
